import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppSelector } from '../../../../state/state';
import type { RootState } from '../../../../state/state';
import { selectEffectiveUID, selectChequeredEnabled } from '../../../../state/UserStatesV2/userSliceV2';
import ChequeredPromotion from '../SubscriptionDetails/Components/ChequeredPromotion';

// Launch Animation Component
const LaunchAnimation: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
    const [currentStep, setCurrentStep] = useState(0);
    
    const features = [
        { icon: '♟️', title: 'Interactive Puzzles', desc: '30-40 puzzles per category' },
        { icon: '👥', title: 'Real-time Collaboration', desc: 'Live puzzle solving in meetings' },
        { icon: '📚', title: 'Teaching Mode', desc: 'Annotate and explain positions' },
        { icon: '📊', title: 'Progress Tracking', desc: 'Monitor student development' },
        { icon: '🎯', title: 'Categorized Learning', desc: 'Tactics, Openings, Endgames & more' }
    ];

    useEffect(() => {
        const timer = setTimeout(() => {
            if (currentStep < features.length) {
                setCurrentStep(prev => prev + 1);
            } else {
                setTimeout(() => onComplete(), 500);
            }
        }, 600);
        return () => clearTimeout(timer);
    }, [currentStep, features.length, onComplete]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-gradient-to-br from-purple-900 via-indigo-900 to-purple-800 flex items-center justify-center"
        >
            <div className="text-center px-6 max-w-4xl mx-auto">
                {/* Main Title */}
                <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                >
                    <motion.div
                        animate={{ 
                            rotate: [0, 5, -5, 0],
                            scale: [1, 1.1, 1]
                        }}
                        transition={{ 
                            duration: 2,
                            repeat: Infinity,
                            repeatType: "reverse"
                        }}
                        className="text-8xl mb-6"
                    >
                        ♟️
                    </motion.div>
                    <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-100 to-white">
                        World's Best
                    </h1>
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-2">
                        Chess Teaching Platform
                    </h2>
                    <p className="text-xl text-purple-200 mt-4 mb-12">
                        Professional tools for chess academies
                    </p>
                </motion.div>

                {/* Feature Highlights */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-12">
                    {features.map((feature, index) => (
                        <motion.div
                            key={feature.title}
                            initial={{ opacity: 0, y: 20, scale: 0.9 }}
                            animate={{ 
                                opacity: index <= currentStep ? 1 : 0.3,
                                y: index <= currentStep ? 0 : 20,
                                scale: index <= currentStep ? 1 : 0.9
                            }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20"
                        >
                            <div className="text-3xl mb-2">{feature.icon}</div>
                            <h3 className="text-white font-semibold text-sm mb-1">{feature.title}</h3>
                            <p className="text-purple-200 text-xs">{feature.desc}</p>
                        </motion.div>
                    ))}
                </div>

                {/* Loading Bar */}
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 3, ease: "easeInOut" }}
                    className="mt-12 h-1 bg-white/30 rounded-full overflow-hidden"
                >
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: '100%' }}
                        transition={{ duration: 3, ease: "easeInOut" }}
                        className="h-full bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400"
                    />
                </motion.div>
            </div>
        </motion.div>
    );
};

const ANIMATION_STORAGE_KEY = 'chequered-animation-last-shown';
const FOUR_HOURS_MS = 4 * 60 * 60 * 1000; // 4 hours in milliseconds

const ChequeredLandingPage: React.FC = () => {
    const { subscriptionId } = useParams<{ subscriptionId: string }>();
    const location = useLocation();
    const fromPath = (location.state as any)?.from || '/mysubscriptions';
    const navigate = useNavigate();
    const userData = useAppSelector((state: RootState) => state.userSliceV2.data);
    
    // Get chequeredEnabled flag from user state (fetched from active service provider)
    const chequeredEnabled = useAppSelector(selectChequeredEnabled);
    const isLoadingFeatureFlag = !userData; // Loading if userData is not yet available
    
    // Detect mobile device
    const [isMobile, setIsMobile] = useState(false);
    const [showMobileWarning, setShowMobileWarning] = useState(true);
    
    useEffect(() => {
        const checkMobile = () => {
            const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            const smallScreen = window.innerWidth < 768;
            setIsMobile(mobile || smallScreen);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    
    // Check if animation should be shown (only if 4+ hours have passed since last shown)
    const [showLaunchAnimation, setShowLaunchAnimation] = useState(() => {
        try {
            if (typeof window === 'undefined') {
                return true; // SSR: default to showing
            }

            const lastShownRaw = localStorage.getItem(ANIMATION_STORAGE_KEY);
            
            // If never shown before, show animation
            if (!lastShownRaw) {
                console.log('[CheQuered] Animation: Never shown before, showing now');
                return true;
            }

            const lastShown = parseInt(lastShownRaw, 10);
            const now = Date.now();

            // If invalid timestamp, show animation
            if (Number.isNaN(lastShown) || lastShown <= 0) {
                console.log('[CheQuered] Animation: Invalid timestamp, showing now');
                return true;
            }

            const timeSinceLastShown = now - lastShown;
            const shouldShow = timeSinceLastShown >= FOUR_HOURS_MS;

            console.log('[CheQuered] Animation check:', {
                lastShown: new Date(lastShown).toLocaleString(),
                now: new Date(now).toLocaleString(),
                hoursSince: (timeSinceLastShown / (60 * 60 * 1000)).toFixed(2),
                shouldShow
            });

            return shouldShow;
        } catch (e) {
            console.error('[CheQuered] Error checking localStorage:', e);
            return true; // On error, default to showing
        }
    });

    // Primary features (main actions)
    const primaryFeatures = [
        {
            id: 'chess-basics',
            title: 'Chessopedia',
            icon: '📚',
            description: 'Comprehensive chess encyclopedia with articles, strategies, and educational content',
            color: 'from-pink-500 to-rose-500',
            path: `/chequered/${subscriptionId}/chess-basics`,
            badge: 'Knowledge Base'
        },
        {
            id: 'subscription-data',
            title: 'Class Puzzles',
            icon: '👥',
            description: 'Manage puzzles for this class, track student progress, and import from main library',
            color: 'from-green-500 to-emerald-500',
            path: `/chequered/${subscriptionId}/subscription-data`,
            badge: 'Class Management'
        }
    ];

    // Secondary features (tools)
    const secondaryFeatures = [
        {
            id: 'puzzles',
            title: 'Puzzles',
            icon: '🎯',
            description: 'Browse and manage chess puzzles by category, difficulty, and type',
            color: 'from-purple-500 to-pink-500',
            path: `/chequered/${subscriptionId}/puzzles`
        },
        {
            id: 'teaching',
            title: 'Teaching Board',
            icon: '📖',
            description: 'Interactive board for live teaching and demonstrations',
            color: 'from-blue-500 to-cyan-500',
            path: `/chequered/${subscriptionId}/teaching`
        },
        {
            id: 'neo',
            title: 'NEO',
            icon: '✨',
            description: 'Lichess-style board editor for teaching and demonstrations',
            color: 'from-indigo-500 to-violet-600',
            path: `/chequered/${subscriptionId}/teaching/neo`
        },
        {
            id: 'analysis',
            title: 'Game Analysis',
            icon: '🔍',
            description: 'Analyze chess games with Stockfish engine',
            color: 'from-orange-500 to-red-500',
            path: `/chequered/${subscriptionId}/analysis`
        }
    ];

    const handleLaunchComplete = () => {
        setShowLaunchAnimation(false);
        // Store timestamp so we only show again after 4 hours
        try {
            const now = Date.now();
            localStorage.setItem(ANIMATION_STORAGE_KEY, now.toString());
            console.log('[CheQuered] Animation completed, timestamp saved:', new Date(now).toLocaleString());
        } catch (e) {
            console.error('[CheQuered] Error saving animation timestamp:', e);
        }
    };

    // Show loading state while checking feature flag
    if (isLoadingFeatureFlag) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50 to-blue-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading Chequered...</p>
                </div>
            </div>
        );
    }
    
    // Handle feature click - show promotion if not enabled
    const handleFeatureClick = (path: string) => {
        if (!chequeredEnabled) {
            // Show promotion modal or toast
            return;
        }
        navigate(path, { replace: true, state: { from: fromPath } });
    };
    
    return (
        <>
            <AnimatePresence>
                {showLaunchAnimation && (
                    <LaunchAnimation onComplete={handleLaunchComplete} />
                )}
            </AnimatePresence>
            
            {!showLaunchAnimation && (
                <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50 to-blue-50 p-4 sm:p-8">
                    <div className="max-w-7xl mx-auto">
                {/* Back Button */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4 }}
                    className="mb-6"
                >
                    <button
                        onClick={() => navigate(fromPath, { replace: true })}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        <span className="font-medium">Back to Subscription</span>
                    </button>
                </motion.div>
                
                {/* Hero Section */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12"
                >
                    <div className="inline-block mb-4">
                        <motion.div
                            animate={{ rotate: [0, 10, -10, 0] }}
                            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                            className="text-6xl sm:text-8xl"
                        >
                            ♟️
                        </motion.div>
                    </div>
                    <h1 className="text-4xl sm:text-6xl font-bold text-gray-900 mb-4">
                        <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-clip-text text-transparent">
                            Che<span className="text-purple-700">Q</span>uered
                        </span>
                    </h1>
                    <p className="text-xl sm:text-2xl text-gray-600 max-w-3xl mx-auto">
                        Your complete chess teaching and puzzle management platform
                    </p>
                    
                    {/* Mobile Warning Banner */}
                    {isMobile && showMobileWarning && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="mt-6 mx-auto max-w-2xl"
                        >
                            <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-4 shadow-lg relative">
                                <button
                                    onClick={() => setShowMobileWarning(false)}
                                    className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M18 6L6 18M6 6l12 12"/>
                                    </svg>
                                </button>
                                <div className="flex items-start gap-3 pr-6">
                                    <div className="flex-shrink-0">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-600">
                                            <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                                            <line x1="12" y1="18" x2="12" y2="18"></line>
                                        </svg>
                                    </div>
                                    <div className="flex-1 text-left">
                                        <p className="text-blue-900 font-semibold text-sm sm:text-base">
                                            📱 Best Experience on Tablet or Desktop
                                        </p>
                                        <p className="text-blue-700 text-xs sm:text-sm mt-1">
                                            Chequered works best on tablets or desktop devices for optimal chess board interaction and feature access.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                    
                    {/* Feature Not Enabled Banner */}
                    {!chequeredEnabled && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="mt-6 mx-auto max-w-2xl"
                        >
                            <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 shadow-lg">
                                <div className="flex items-center justify-center gap-3">
                                    <div className="flex-shrink-0">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-600">
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                        </svg>
                                    </div>
                                    <div className="flex-1 text-left">
                                        <p className="text-amber-900 font-semibold text-sm sm:text-base">
                                            🔒 Features Locked - Not Enabled
                                        </p>
                                        <p className="text-amber-700 text-xs sm:text-sm mt-1">
                                            Contact HoSaksham team to unlock: <strong>support@hosaksham.com</strong> or <strong>+91 6363994715</strong>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </motion.div>

                {/* Primary Features - Large Cards */}
                <div className="mb-12">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Main Features</h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
                        {primaryFeatures.map((item, index) => {
                            // Chessopedia is always accessible, others require chequeredEnabled
                            const isAccessible = item.id === 'chess-basics' || chequeredEnabled;
                            
                            return (
                                <motion.div
                                    key={item.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    whileHover={isAccessible ? { scale: 1.02, y: -5 } : {}}
                                    whileTap={isAccessible ? { scale: 0.98 } : {}}
                                    onClick={() => isAccessible ? navigate(item.path) : null}
                                    className={`group relative overflow-hidden rounded-2xl bg-white shadow-xl border-2 transition-all duration-300 ${
                                        isAccessible 
                                            ? 'cursor-pointer border-transparent hover:border-purple-300' 
                                            : 'cursor-not-allowed border-gray-200 opacity-60'
                                    }`}
                                >
                                    {!isAccessible && (
                                        <div className="absolute top-4 right-4 z-10 bg-amber-500 rounded-full p-2 shadow-lg">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                            </svg>
                                        </div>
                                    )}
                                    <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 ${isAccessible ? 'group-hover:opacity-10' : ''} transition-opacity duration-300`} />
                                    <div className="relative p-8">
                                        <div className="flex items-start gap-4">
                                            <div className={`text-5xl sm:text-6xl p-4 rounded-2xl bg-gradient-to-br ${item.color} bg-opacity-10 ${isAccessible ? 'group-hover:bg-opacity-20' : ''} transition-all duration-300`}>
                                                {item.icon}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <h2 className={`text-2xl sm:text-3xl font-bold text-gray-900 ${isAccessible ? 'group-hover:text-purple-600' : ''} transition-colors`}>
                                                        {item.title}
                                                    </h2>
                                                    {item.badge && (
                                                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                                                            {item.badge}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                                                    {item.description}
                                                </p>
                                            </div>
                                            <div className={`text-gray-400 ${isAccessible ? 'group-hover:text-purple-500' : ''} transition-colors`}>
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>

                {/* Secondary Features - Tools Grid */}
                <div className="mb-12">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Tools & Utilities</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                        {secondaryFeatures.map((item, index) => (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: (index + primaryFeatures.length) * 0.1 }}
                                whileHover={chequeredEnabled ? { scale: 1.05, y: -5 } : {}}
                                whileTap={chequeredEnabled ? { scale: 0.98 } : {}}
                                onClick={() => chequeredEnabled ? navigate(item.path) : null}
                                className={`group relative overflow-hidden rounded-xl bg-white shadow-lg border-2 transition-all duration-300 ${
                                    chequeredEnabled 
                                        ? 'cursor-pointer border-transparent hover:border-purple-300' 
                                        : 'cursor-not-allowed border-gray-200 opacity-60'
                                }`}
                            >
                                {!chequeredEnabled && (
                                    <div className="absolute top-3 right-3 z-10 bg-amber-500 rounded-full p-1.5 shadow-lg">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                        </svg>
                                    </div>
                                )}
                                <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 ${chequeredEnabled ? 'group-hover:opacity-10' : ''} transition-opacity duration-300`} />
                                <div className="relative p-6">
                                    <div className="flex flex-col items-center text-center">
                                        <div className={`text-4xl sm:text-5xl p-3 rounded-xl bg-gradient-to-br ${item.color} bg-opacity-10 ${chequeredEnabled ? 'group-hover:bg-opacity-20' : ''} transition-all duration-300 mb-4`}>
                                            {item.icon}
                                        </div>
                                        <h3 className={`text-xl font-bold text-gray-900 mb-2 ${chequeredEnabled ? 'group-hover:text-purple-600' : ''} transition-colors`}>
                                            {item.title}
                                        </h3>
                                        <p className="text-gray-600 text-sm leading-relaxed">
                                            {item.description}
                                        </p>
                                        <div className={`mt-4 text-gray-400 ${chequeredEnabled ? 'group-hover:text-purple-500' : ''} transition-colors`}>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Footer Info */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="mt-12 text-center text-gray-500 text-sm"
                >
                    <p>Select an option above to get started</p>
                </motion.div>
            </div>
        </div>
            )}
        </>
    );
};

export default ChequeredLandingPage;

